import { MouseEvent } from 'react';
import { format } from 'date-fns';
import { WPMedia, WPPost } from '../../types/wordpress';
import Card from '../ui/Card';
import QuietAction from '../cards/QuietAction';
import PlaceLink from '../layout/PlaceLink';
import SaveToMyOrbconButton from '../saved/SaveToMyOrbconButton';
import { resourceOffer } from '../../utils/cardFacts';
import { isUnderRevision } from '../../utils/resourceRevision';
import RevisionNotice from './RevisionNotice';
import ResourceKindLabel from './ResourceKindLabel';
import { decodeHtmlEntities } from '../../utils/htmlEntities';
import { buildResourcePath } from '../../utils/resourceLinks';
import { rememberResourcePost } from '../../utils/placeMotion';

interface ResourceRowProps {
  post?: WPPost;
  id?: number;
  title?: string;
  slug?: string;
  date?: string | null;
  /** Shown before the date, for example Updated. */
  datePrefix?: string;
  underRevision?: boolean;
  attachments?: WPMedia[];
  /** Header search uses its own place transition so the panel can close. */
  onActivate?: (origin: HTMLElement) => void;
}

export default function ResourceRow({
  post,
  id,
  title: titleProp,
  slug: slugProp,
  date: dateProp,
  datePrefix,
  underRevision: underProp,
  attachments = [],
  onActivate,
}: ResourceRowProps) {
  const title = titleProp ?? (post ? decodeHtmlEntities(post.title.rendered) : '');
  const slug = slugProp ?? post?.slug ?? '';
  const postId = id ?? post?.id;
  const to = buildResourcePath(slug);
  const underRevision = underProp ?? (post ? isUnderRevision(post.acf?.under_revision) : false);
  const offer = resourceOffer(attachments);
  const rawDate = dateProp ?? post?.date;
  const formatted = rawDate ? format(new Date(rawDate), 'MMMM d, yyyy') : '';
  const date = formatted && datePrefix ? `${datePrefix} ${formatted}` : formatted;
  const meta = (
    <>
      {(underRevision || date || offer) && (
        <div className="mt-2">
          {underRevision && <RevisionNotice className="mb-1" />}
          {(date || offer) && (
            <p className="text-sm text-gray-600">{date && offer ? `${date} · ${offer}` : date || offer}</p>
          )}
        </div>
      )}
    </>
  );
  const save =
    typeof postId === 'number' ? (
      <SaveToMyOrbconButton
        type="RESOURCE"
        postId={postId}
        title={title}
        slug={slug}
        variant="icon"
        align="end"
      />
    ) : null;

  const open = (event: MouseEvent<HTMLAnchorElement>) => {
    if (onActivate) {
      event.preventDefault();
      onActivate(event.currentTarget);
      return;
    }
    if (post) rememberResourcePost(post);
  };

  // Header search keeps a denser whole-row hit target; home uses the Resources card pattern.
  if (onActivate) {
    return (
      <div className="flex items-start gap-3 w-full p-3 bg-white border border-gray-200 rounded-lg">
        <PlaceLink
          to={to}
          surface={{ kind: 'resource', slug, via: 'search' }}
          ariaLabel={title}
          onClick={open}
          className="min-w-0 flex-1 min-h-[44px] text-left focus-ring rounded-sm"
        >
          <ResourceKindLabel postId={postId} title={title} />
          <p className="mt-0.5 text-base font-semibold text-gray-900">{title}</p>
          {meta}
        </PlaceLink>
        {save}
      </div>
    );
  }

  return (
    <Card className="!p-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <ResourceKindLabel postId={postId} title={title} />
          <h3 className="mt-0.5 text-base font-semibold text-gray-900">{title}</h3>
        </div>
        {save}
      </div>
      {meta}
      <div className="mt-2">
        <span className="inline-flex min-h-[44px] items-center">
          <QuietAction to={to} label={`Open ${title}`} onClick={open}>
            Open
          </QuietAction>
        </span>
      </div>
    </Card>
  );
}
